import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox, Mail, Phone, Trash2, Check, RefreshCw, Circle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import UCLogo from "@/components/site/UCLogo";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s] = await Promise.all([api.get("/enquiries"), api.get("/enquiries/stats")]);
      setEnquiries(e.data);
      setStats(s.data);
      if (e.data.length && !selected) setSelected(e.data[0]);
    } catch {
      toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (item) => {
    const status = item.status === "new" ? "read" : "new";
    await api.patch(`/enquiries/${item.id}`, { status });
    load();
    if (selected?.id === item.id) setSelected({ ...item, status });
  };

  const remove = async (id) => {
    await api.delete(`/enquiries/${id}`);
    toast.success("Enquiry deleted.");
    setSelected(null);
    load();
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-concrete-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UCLogo className="h-8 w-8 text-concrete-900" />
            <div className="leading-none">
              <p className="font-display font-extrabold tracking-tight text-sm text-concrete-900">DASHBOARD</p>
              <p className="overline text-concrete-500">Enquiries</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block overline text-concrete-500">{user?.email}</span>
            <button onClick={load} data-testid="refresh-btn" className="p-2 hover:bg-concrete-100 transition-colors" aria-label="Refresh">
              <RefreshCw className="h-4 w-4 text-concrete-700" />
            </button>
            <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-2 text-sm border border-concrete-900 px-4 py-2 hover:bg-concrete-900 hover:text-white transition-colors">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: "Total", value: stats.total },
            { label: "New", value: stats.new },
            { label: "Read", value: stats.read },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-black/10 p-6" data-testid={`stat-${s.label.toLowerCase()}`}>
              <p className="overline text-concrete-500">{s.label}</p>
              <p className="mt-2 font-display font-extrabold text-4xl text-concrete-900">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* List */}
          <div className="lg:col-span-5 bg-white border border-black/10">
            <div className="p-4 border-b border-black/10 flex items-center gap-2">
              <Inbox className="h-4 w-4 text-concrete-500" />
              <span className="overline text-concrete-700">Inbox</span>
            </div>
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-black/10" data-testid="enquiry-list">
              {loading && <p className="p-6 text-sm text-concrete-500">Loading…</p>}
              {!loading && enquiries.length === 0 && (
                <p className="p-6 text-sm text-concrete-500">No enquiries yet.</p>
              )}
              {enquiries.map((e) => (
                <button
                  key={e.id}
                  onClick={() => setSelected(e)}
                  data-testid={`enquiry-item-${e.id}`}
                  className={`w-full text-left p-4 hover:bg-concrete-50 transition-colors ${selected?.id === e.id ? "bg-concrete-100" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display font-bold text-concrete-900 truncate">{e.name}</span>
                    {e.status === "new" && <Circle className="h-2.5 w-2.5 fill-concrete-900 text-concrete-900 shrink-0" />}
                  </div>
                  <p className="text-xs text-concrete-500 mt-1 truncate">{e.service} · {fmt(e.created_at)}</p>
                  <p className="text-sm text-concrete-600 mt-1 truncate">{e.message}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="lg:col-span-7 bg-white border border-black/10 p-7" data-testid="enquiry-detail">
            {!selected ? (
              <p className="text-sm text-concrete-500">Select an enquiry to view details.</p>
            ) : (
              <div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-display font-extrabold text-3xl tracking-tight text-concrete-900">{selected.name}</h2>
                    <p className="overline text-concrete-500 mt-2">{selected.service} · {fmt(selected.created_at)}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => markRead(selected)} data-testid="mark-read-btn" className="flex items-center gap-1.5 text-sm border border-black/20 px-3 py-2 hover:bg-concrete-900 hover:text-white transition-colors">
                      <Check className="h-4 w-4" /> {selected.status === "new" ? "Mark read" : "Mark new"}
                    </button>
                    <button onClick={() => remove(selected.id)} data-testid="delete-btn" className="flex items-center gap-1.5 text-sm border border-destructive/40 text-destructive px-3 py-2 hover:bg-destructive hover:text-white transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-sm bg-concrete-100 px-4 py-2 hover:bg-concrete-200 transition-colors">
                    <Phone className="h-4 w-4" /> {selected.phone}
                  </a>
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-sm bg-concrete-100 px-4 py-2 hover:bg-concrete-200 transition-colors">
                    <Mail className="h-4 w-4" /> {selected.email}
                  </a>
                </div>

                <div className="mt-8 border-t border-black/10 pt-6">
                  <p className="overline text-concrete-500 mb-3">Message</p>
                  <p className="text-concrete-800 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
